'use client';

import React, { ReactNode, useCallback } from 'react';
import Box from '@mui/material/Box';
import { sp } from '@/theme/mui';
import color from '@/theme/color';
import elevation from '@/theme/elevation';

import { Chip } from '@/components/atoms/Chip/Chip';
import { Title } from '@/components/atoms/Title/Title';

const imgNoSelect = {
  '& > img': {
    objectFit: 'cover',
    userSelect: 'none',
    WebkitUserDrag: 'none',
    MozUserSelect: 'none',
    WebkitUserSelect: 'none',
    msUserSelect: 'none',
  },
} as const;

const getCardColorSx = (
  cardColor: string,
  isBordered: boolean,
  isTransparent: boolean,
) => {
  if (cardColor === 'none') return { background: 'none' };
  if (cardColor === 'white') {
    return {
      background: '#faf7f3',
      color: '#000000',
      ...(isBordered && { border: `1px solid ${color.cards.green.secondary}` }),
    };
  }
  if (cardColor === 'green') {
    return { background: color.cards.green.secondary, color: '#000000' };
  }
  if (cardColor === 'yellow') {
    return {
      background: color.cards.buttermilk.primary,
      color: '#000000',
      ...(isBordered && { border: `1px solid ${color.cards.green.secondary}` }),
    };
  }
  const cards = color.cards as Record<
    string,
    { primary: string; secondary: string; tertiary: string }
  >;
  const card = cards[cardColor];
  if (!card) return {};
  return {
    background: `${card.primary}${isTransparent ? 'F2' : 'FF'}`,
    ...(isBordered
      ? { border: `1px solid ${card.secondary}` }
      : { border: 'none' }),
    ...(isBordered && { color: card.tertiary }),
  };
};

export interface CardProps {
  className?: string;
  elevation?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  children?: React.ReactNode;
  color?: any;
  isPaddingSm?: boolean;
  isBordered?: boolean;
  isNoPadding?: boolean;
  isTransparent?: boolean;
  isFullWidth?: boolean;
  as?: React.ElementType;
  title?: string;
  header?: ReactNode;
  footer?: ReactNode;
  to?: string;
  onNavigate?: (to: string) => void;
  topChip?: string;
}

export const Card = ({
  className,
  elevation: elev = 2,
  children,
  color: cardColor = 'white',
  isFullWidth = false,
  isPaddingSm = false,
  isNoPadding = false,
  isTransparent = false,
  isBordered = true,
  as: Component = 'div',
  title,
  header,
  footer,
  to,
  onNavigate,
  topChip,
}: CardProps) => {
  const Wrapper = useCallback(
    ({ children }: { children: ReactNode }) =>
      to ? (
        <Box
          onClick={() => {
            if (onNavigate) {
              onNavigate(to);
            } else {
              window.location.href = to;
            }
          }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            overflow: 'hidden',
            cursor: 'pointer',
            userSelect: 'none',
            textDecoration: 'none',
            borderRadius: sp[8],
            maxHeight: 'fit-content',
            p: sp[16],
            pt: sp[48],
            ...(isFullWidth && { p: 0, pt: 0 }),
            position: 'relative',
            border: `1px solid #cbc5b9`,
            background: '#ffffff',
            boxShadow: elevation[1],
            transitionDuration: '0.2s',
            '&:hover': { transform: 'scale(1.02)' },
            flex: 1,
          }}
        >
          {children}
        </Box>
      ) : (
        <>{children}</>
      ),
    [to, onNavigate, isFullWidth],
  );

  return (
    <Wrapper>
      <Box
        component={Component}
        className={className}
        sx={{
          background: '#ffffff',
          ...(!isFullWidth && { borderRadius: sp[8] }),
          boxShadow: elevation[elev],
          ...(isBordered && cardColor === 'white'
            ? {}
            : { border: isBordered ? '1px solid #d9d9d9' : undefined }),
          boxSizing: 'border-box',
          textDecoration: 'none',
          width: isFullWidth ? '100%' : 'auto',
          p: '36px',
          ...(isPaddingSm && { p: sp[16] }),
          ...getCardColorSx(cardColor, isBordered, isTransparent),
          ...(isNoPadding && { p: 0 }),
        }}
      >
        {topChip && (
          <Box
            sx={{
              typography: 'bodySm',
              color: '#000000',
              position: 'absolute',
              left: 0,
              top: 0,
            }}
          >
            <Chip isRectangle label={topChip} />
          </Box>
        )}
        {header && (
          <Box
            sx={{
              position: 'relative',
              ...imgNoSelect,
              '& > img': {
                ...imgNoSelect['& > img'],
                width: '100%',
                height: '148px',
              },
            }}
          >
            {header}
          </Box>
        )}
        {title && (
          <Box sx={{ typography: 'bodySm', p: sp[8] }}>
            <Title size='xs' isNoMargin>
              {title}
            </Title>
          </Box>
        )}
        {children}
        {footer && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row-reverse',
              ...imgNoSelect,
              '& > img': { ...imgNoSelect['& > img'], height: '180px' },
            }}
          >
            {footer}
          </Box>
        )}
      </Box>
    </Wrapper>
  );
};

Card.displayName = 'Card';

export default Card;
